import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Spin, Skeleton, message, Button } from "antd";
import { ReactComponent as Back } from "../../../../assets/svgs/back-button.svg";
import { API_URLS } from "../../../../api/apiUrls";
import { getApi } from "../../../../api/api_instance";
import { QuestionForm } from "./components/questionForm";
import { SearchBar } from "../../../../components/Elements/Search/SearchBar";
import { useLocation } from "react-router-dom";
import { useParams } from "react-router-dom";
import TextStyles from "../../../../components/Elements/TextStyles/TextStyles";
import { ArrowUpOutlined } from "@ant-design/icons";

const AddQuestions = () => {
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedSection, setSelectedSection] = useState(null);
  const [subsections, setSubsections] = useState([]);
  const [selectedSubsection, setSelectedSubsection] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [expandAll, setExpandAll] = useState(false);
  const [showGoTop, setShowGoTop] = useState(false);
  const pageSize = 20;
  const navigate = useNavigate();
  const location = useLocation();
  const { formTitle } = location.state || {};

  const scrollableContainerRef = useRef(null);

  const scrollableContainer = {
    maxHeight: "1000px",
    overflowY: "auto",
    padding: "10px",
    borderRadius: "8px",
  };

  const { formId } = useParams();
  useEffect(() => {
    fetchSections();
  }, []);
  useEffect(() => {
    fetchSections();

    // ✅ Simple window scroll listener
    const handleScroll = () => {
      setShowGoTop(window.scrollY > 300);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const handleContainerScroll = () => {
      if (scrollableContainerRef.current) {
        const scrollTop = scrollableContainerRef.current.scrollTop;
        setShowGoTop((prev) => prev || scrollTop > 200);
      }
    };

    const containerElement = scrollableContainerRef.current;
    if (containerElement) {
      containerElement.addEventListener("scroll", handleContainerScroll);
      return () =>
        containerElement.removeEventListener("scroll", handleContainerScroll);
    }
  }, [questions, selectedSubsection]); // Re-attach when content changes

  const scrollToTop = () => {
    // First scroll the container to top
    if (scrollableContainerRef.current) {
      scrollableContainerRef.current.scrollTop = 0;
    }
    // Then scroll window to top
    window.scrollTo({ top: 0, behavior: "smooth" });
    // Hide the button immediately
    setShowGoTop(false);
  };

  const fetchSections = async () => {
    setLoading(true);
    try {
      const response = await getApi({
        apiUrl: API_URLS.ALL_QUESTIONSPERSECTION(pageSize),
      });
      if (response.success) {
        setSections(response.data);
        if (response.data.length > 0) {
          const firstSection = response.data[0];
          setSelectedSection(firstSection.section);
          fetchSubsections(firstSection.section);
        }
      } else {
        console.error("Failed to fetch sections");
      }
    } catch (error) {
      console.error("Error fetching sections", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSubsections = async (sectionId) => {
    setLoading(true);
    try {
      const response = await getApi({
        apiUrl: API_URLS.SUBSECTION_BY_ID(sectionId),
      });
      if (response.success) {
        setSubsections(response.data);
        setSelectedSection(sectionId);

        if (response.data.length > 0) {
          const firstSubsection = response.data[0];
          setSelectedSubsection(firstSubsection.id);
          fetchQuestions(firstSubsection.id);
        }
      } else {
        console.error("Failed to fetch subsections");
      }
    } catch (error) {
      console.error("Error fetching subsections", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchQuestions = useCallback(
    async (subsectionId = selectedSubsection) => {
      if (!subsectionId) return;

      setLoading(true);
      try {
        const response = await getApi({
          apiUrl: API_URLS.QUESTIONS_BY_SUBSECTION(subsectionId, searchTerm),
        });

        if (response.success) {
          console.log("Questions (raw response):", response.data);
          setQuestions(response.data);
        } else {
          setQuestions([]);
        }
      } catch (error) {
        console.error("Error fetching questions", error);
        message.error("Failed to fetch questions");
      } finally {
        setLoading(false);
      }
    },
    [searchTerm],
  );

  useEffect(() => {
    fetchQuestions();
  }, [fetchQuestions]);

  useEffect(() => {
    console.log("Received formId:", formId);
  }, [formId]);

  return (
    <div className="h-full px-[20px] mb-6">
      <div className="flex justify-end text-xs font-light h-4 ">
        <p>Home</p>
        <span>/</span>
        <p>Dynamic Form</p>
        <span>/</span>
        <TextStyles className="text-[#06513D]">{formTitle}</TextStyles>
      </div>

      <div className="flex justify-between h-12 pb-2">
        <div className="flex items-center">
          <Back className="cursor-pointer " onClick={() => navigate(-1)} />
          <h3 className="text-[#06513D] mt-1 text-xl">
            {formTitle || "demolastform"}
          </h3>
        </div>
      </div>

      <div className="w-full bg-[#fff]  shadow-[0_0_16px_0_#3BA2B91F] mb-6">
        <div className="grid grid-cols-5 border border-[#3BA2B959] rounded-t-[10px] overflow-hidden p-1 max-h-[150px] overflow-y-auto">
          {sections.map((section) => (
            <div
              key={section.id}
              className={`
                
        relative
        h-10
        px-2
        flex
        items-center
        justify-center
        text-[13px]
        font-medium
        cursor-pointer
        rounded-xl
        mx-1
        my-1
        transition-all
        duration-200
        ease-in-out
        border border-[#3BA2B959] ${
          selectedSection === section.section
            ? "bg-[#06513D] text-white shadow-md transform scale-105"
            : "bg-transparent text-gray-700 hover:text-gray-900"
        }`}
              onClick={() => fetchSubsections(section.section)}
            >
              <span className="text-center leading-tight">{section.name}</span>
            </div>
          ))}
        </div>
        {/* <hr className="border-t-[1px] border-[#3BA2B959] shadow-[0_0_16px_0_#3BA2B91F]" /> */}

        {selectedSection && (
          <div className="">
            <div className="flex flex-wrap gap-5  p-2 text-[11px] font-normal border border-[#3BA2B91C] shadow-[0_0_25.2px_0_#3BA2B91F]">
              {subsections.length > 0 ? (
                subsections.map((sub) => (
                  <div
                    key={sub.id}
                    className={`p-3 rounded-xl cursor-pointer border transition-all duration-200  ${
                      selectedSubsection === sub.id
                        ? "bg-[#E6EEEC]  shadow-md "
                        : "bg-transparent text-black hover:bg-gray-100 "
                    }`}
                    onClick={() => {
                      setSelectedSubsection(sub.id);
                      fetchQuestions(sub.id);
                    }}
                  >
                    {sub.name}
                  </div>
                ))
              ) : (
                <p className="text-gray-500">No subsections available</p>
              )}
            </div>
          </div>
        )}
      </div>

      <div className="bg-[#fff]  w-full border border-[#3BA2B959] rounded-[10px] pt-5 shadow-[0_0_16px_0_#3BA2B91F] mb-6 relative">
        <div className="">
          <div className="flex items-center gap-5 ml-10 mb-3">
            <div className="flex items-center justify-center h-[42px] px-6 border border-[#06513D] bg-[#B6D4D933] rounded-md text-[#06513D] text-base font-light">
              TOTAL QUESTION
            </div>
            <div className="flex-shrink-0">
              <SearchBar
                width="300px"
                onSearch={(value) => setSearchTerm(value)}
              />
            </div>
            <Button
              type="primary"
              style={{ background: "#06513D" }}
              className="text-[13px] font-light px-8 py-6 ml-44 "
              onClick={() => setExpandAll((prev) => !prev)}
            >
              {expandAll ? "Collapse All" : "Expand All"}
            </Button>
          </div>

          <div style={scrollableContainer}>
            {loading ? (
              <div className="flex flex-col gap-4 p-4">
                <Skeleton.Button block active />
                <Skeleton.Button block active />
                <Skeleton.Button block active />
                <Skeleton.Button block active />
              </div>
            ) : (
              selectedSubsection && (
                <div className="mx-10 shadow-[0_0_16px_0_#3BA2B91F]">
                  {questions.length > 0 ? (
                    <QuestionForm
                      formId={formId}
                      sectionId={selectedSection}
                      subSectionId={selectedSubsection}
                      questions={questions}
                      expandAll={expandAll}
                    />
                  ) : (
                    <p className="text-gray-500 text-center">
                      No questions available.
                    </p>
                  )}
                </div>
              )
            )}
          </div>
          {/* Scroll to Top Button - Positioned within the white div */}
          {showGoTop && questions.length > 0 && (
            <Button
              type="primary"
              shape="circle"
              icon={<ArrowUpOutlined />}
              size="large"
              onClick={scrollToTop}
              style={{
                position: "absolute",
                bottom: "20px",
                right: "20px",
                background: "#06513D",
                zIndex: 1000,

                boxShadow: "0 4px 12px rgba(0,0,0,0.3)",
              }}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default AddQuestions;
